import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listFacilities, listTenantsOptions, createFacility } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useAuth, tenantIdsWithRole } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/facilities")({ component: FacilitiesPage });

function FacilitiesPage() {
  const qc = useQueryClient();
  const { roles } = useAuth();
  const { isSuperAdmin: su } = useScope();
  const adminTenantIds = tenantIdsWithRole(roles, "tenant_admin");

  const fetchTenants = useServerFn(listTenantsOptions);
  const fetchFacilities = useServerFn(listFacilities);
  const createFn = useServerFn(createFacility);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [tenantId, setTenantId] = useState<string>("");
  const [tz, setTz] = useState("UTC");
  const [submitting, setSubmitting] = useState(false);

  const { data: tenants } = useQuery({
    queryKey: ["tenants-options"],
    queryFn: () => runServerFn(() => fetchTenants(), "Loading tenants"),
  });

  const allowedTenants = useMemo(() => {
    if (!tenants) return [];
    if (su) return tenants;
    return tenants.filter((t: any) => adminTenantIds.includes(t.id));
  }, [tenants, su, adminTenantIds]);

  const { data: facilities, isLoading } = useQuery({
    queryKey: ["facilities"],
    queryFn: () => runServerFn(() => fetchFacilities(), "Loading facilities"),
  });

  const tenantName = (id: string) => tenants?.find((t: any) => t.id === id)?.name ?? "—";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !tenantId) return;
    setSubmitting(true);
    try {
      await runServerFn(() => createFn({ data: { tenantId, name: name.trim(), timezone: tz || "UTC" } }), "Creating facility");
      toast.success("Facility created");
      setOpen(false); setName(""); setTenantId(""); setTz("UTC");
      qc.invalidateQueries({ queryKey: ["facilities"] });
    } catch { /* toast handled */ }
    finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Facilities</h2>
          <p className="text-sm text-muted-foreground">Physical warehouse locations.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={allowedTenants.length === 0}><Plus className="h-4 w-4 mr-2" />New facility</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create facility</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Tenant</Label>
                <Select value={tenantId} onValueChange={setTenantId}>
                  <SelectTrigger><SelectValue placeholder="Select a tenant" /></SelectTrigger>
                  <SelectContent>
                    {allowedTenants.map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="f-name">Name</Label>
                <Input id="f-name" required value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="f-tz">Timezone</Label>
                <Input id="f-tz" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="UTC" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting || !tenantId}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Tenant</TableHead><TableHead>Timezone</TableHead><TableHead>Created</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : facilities && facilities.length > 0 ? (
              facilities.map((f: any) => (
                <TableRow key={f.id}>
                  <TableCell className="font-medium">{f.name}</TableCell>
                  <TableCell className="text-muted-foreground">{tenantName(f.tenant_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{f.timezone}</TableCell>
                  <TableCell className="text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No facilities yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
